import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Crown, CreditCard, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CosmeticFrame } from '@/components/economy/Cosmetics';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useConsolidatedPersonality } from '@/hooks/useConsolidatedPersonality';
import { cn } from '@/lib/utils';

export const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { plan } = useSubscription();
  const { profile } = useConsolidatedPersonality();

  const email = user?.email ?? 'Signed in';
  const initials = (email[0] ?? 'U').toUpperCase();
  const avatarUrl: string | undefined =
    (user?.user_metadata as any)?.avatar_url ?? undefined;
  const mbti = profile?.mappings?.mbti as string | undefined;
  const isPro = plan === 'pro';

  if (!user) {
    return (
      <Button size="sm" onClick={() => navigate('/auth')} className="h-9">
        Sign in
      </Button>
    );
  }

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11 md:h-9 md:w-9 rounded-full p-0"
          aria-label="Account menu"
        >
          <CosmeticFrame>
            <Avatar className="h-9 w-9 md:h-8 md:w-8">
              <AvatarImage src={avatarUrl} alt={email} />
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
          </CosmeticFrame>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium truncate">{email}</span>
              <Badge
                variant={isPro ? 'default' : 'outline'}
                className={cn(
                  'text-[10px] uppercase tracking-wide gap-1 shrink-0',
                  isPro && 'bg-primary/15 text-primary border-primary/30'
                )}
              >
                {isPro && <Crown className="w-3 h-3" />}
                {plan ?? 'free'}
              </Badge>
            </div>
            {mbti && (
              <span className="text-xs text-muted-foreground">{mbti}</span>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/profile?tab=profile')}>
          <User className="w-4 h-4 mr-2" />
          Account profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/subscription')}>
          <CreditCard className="w-4 h-4 mr-2" />
          Subscription
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/profile?tab=account')}>
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="w-4 h-4 mr-2" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;